/**
 * AffaireDetailPage — Unified affaire detail view
 * Inspired by V2 DossierDetailPage, built for V1
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { AffaireHeader } from './components/AffaireHeader';
import { AffaireTabBar } from './components/AffaireTabBar';
import { ResumeTab } from './components/ResumeTab';
import { ActiviteTab } from './components/ActiviteTab';
import { CommunicationsTab } from './components/CommunicationsTab';
import { DocumentsTab } from './components/DocumentsTab';
import { FacturationTab } from './components/FacturationTab';
import { ExpertiseV2Tab } from './components/ExpertiseV2Tab';

import { useAffaireDetail } from './hooks/useAffaireDetail';
import './AffaireDetailPage.styles.css';

export const AffaireDetailPage: React.FC = () => {
  const { affaireId } = useParams<{ affaireId: string }>();
  const numericId = affaireId ? parseInt(affaireId, 10) : null;

  const {
    affaire,
    contacts,
    biens,
    paymentData,
    ficheCompletion,
    loading,
    contactsLoading,
    biensLoading,
    activeTab,
    setActiveTab,
    refetchAffaire,
    refetchContacts,
  } = useAffaireDetail(numericId);

  if (loading) {
    return (
      <div className="aff-detail-loading">
        <div className="aff-detail-spinner" />
        <span>Chargement du dossier...</span>
      </div>
    );
  }

  if (!affaire) {
    return (
      <div className="aff-detail-error">
        <span>Dossier introuvable</span>
        <p className="aff-detail-error-sub">Le dossier #{affaireId} n'existe pas ou n'est pas accessible.</p>
      </div>
    );
  }

  // Expertise V2 is a full-screen editor — render it as a full-page takeover
  // (over the admin chrome) with a "Retour à l'app" button, not inside the
  // constrained tab content area.
  if (activeTab === 'fiche-bien' && numericId) {
    return <ExpertiseV2Tab onBack={() => setActiveTab('resume')} />;
  }

  return (
    <div className="aff-detail-page">
      <AffaireHeader
        affaire={affaire}
        onOpenTracker={() => window.open(`/admin/tracker/${affaire.id_unique}`, '_blank')}
      />

      <AffaireTabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      <div className="aff-detail-content">
        {activeTab === 'resume' && (
          <ResumeTab
            affaire={affaire}
            contacts={contacts}
            biens={biens}
            contactsLoading={contactsLoading}
            biensLoading={biensLoading}
            onRefresh={() => { refetchAffaire(); refetchContacts(); }}
            onTabSwitch={setActiveTab}
          />
        )}

        {activeTab === 'activite' && numericId && (
          <ActiviteTab affaireId={numericId} affaire={affaire} />
        )}

        {activeTab === 'communications' && numericId && (
          <CommunicationsTab affaireId={numericId} />
        )}

        {activeTab === 'documents' && numericId && (
          <DocumentsTab affaireId={numericId} />
        )}

        {activeTab === 'facturation' && numericId && (
          <FacturationTab affaireId={numericId} />
        )}

        {/* 'fiche-bien' (Expertise V2) is handled by the full-page early
            return above — see ExpertiseV2Tab. */}


      </div>
    </div>
  );
};
